import { useCallback, useMemo } from "react";
import { JsonParam, useQueryParam } from "use-query-params";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { Options } from "react-select";

export type Filter = {
  [key: string]:
    | Options<SelectOption>
    | string
    | string[]
    | Record<string, unknown>
    | undefined;
};

export function useFilterQuery<F extends Filter>(): {
  filters?: F;
  setFilter: (value?: F) => void;
  toggleFilters: () => void;
  clearFilters: () => void;
  hasFilters: boolean;
  hasFilter: (value: F) => boolean;
  getFilter: (
    key: string,
  ) =>
    | string
    | Options<SelectOption>
    | string[]
    | Record<string, unknown>
    | undefined;
} {
  const [filters, setFilters] = useQueryParam<F | undefined>(
    "filters",
    JsonParam,
  );

  const hasFilters = useMemo(() => {
    return !!filters;
  }, [filters]);

  const setFilter = useCallback(
    (value?: F) => {
      setFilters(value, "pushIn");
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => {
    setFilters(undefined, "replaceIn");
  }, [setFilters]);

  const toggleFilters = useCallback(() => {
    setFilters(!filters ? ({} as F) : undefined, "replaceIn");
  }, [filters, setFilters]);

  const hasFilter = useCallback(
    (value: F) => {
      return (
        !!filters && filters[Object.keys(value)[0]] === Object.values(value)[0]
      );
    },
    [filters],
  );

  const getFilter = useCallback(
    (key: string) => {
      return filters?.[key];
    },
    [filters],
  );

  return {
    filters,
    setFilter,
    hasFilters,
    hasFilter,
    clearFilters,
    toggleFilters,
    getFilter,
  };
}
